import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  acknowledge,
  deleteChannel,
  deleteRule,
  fetchAlert,
  fetchAlerts,
  fetchChannels,
  fetchRules,
  formatDuration,
  formatHold,
  KIND_LABELS,
  saveChannel,
  saveRule,
  testChannel,
  type AlertEvent,
  type AlertItem,
  type Channel,
  type Rule,
  type RuleInput,
  type RuleKind,
  type Severity,
  parseRecipients,
} from './alertsApi';
import { ApiError, fetchSites, type Site } from './fleetApi';
import { linkHandler, routePath } from './router';
import { ConfirmDialog } from './shared/ConfirmDialog';
import { EmptyState } from './shared/EmptyState';
import { FormDialog } from './shared/FormDialog';
import { PageHeader } from './shared/PageHeader';

/** Open alerts refresh this often (the evaluator runs every 30 s). */
const REFRESH_MS = 30_000;

interface AlertsProps {
  apiUrl: string;
  tenantId: string;
  token: string;
  permissions: string[];
  /** The current time (tests). */
  now?: () => Date;
}

type TabName = 'open' | 'history' | 'rules' | 'channels';

function errorText(err: unknown): string {
  return err instanceof ApiError || err instanceof Error ? err.message : String(err);
}

function when(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const SEVERITY: Record<
  Severity,
  { label: string; color: 'error' | 'warning' | 'info'; icon: React.ReactElement }
> = {
  critical: { label: 'Critical', color: 'error', icon: <ErrorIcon /> },
  warning: { label: 'Warning', color: 'warning', icon: <WarningIcon /> },
  info: { label: 'Info', color: 'info', icon: <InfoIcon /> },
};

/** Severity as an icon and a word, never colour alone. */
export function SeverityChip({ severity }: { severity: Severity }) {
  const s = SEVERITY[severity];
  return <Chip size="small" variant="outlined" color={s.color} icon={s.icon} label={s.label} />;
}

/**
 * Alerts (ALR-002/004/005): what is wrong now, what was, the rules that
 * decide, and where notifications go.
 */
export function Alerts({ apiUrl, tenantId, token, permissions, now }: AlertsProps) {
  const [tab, setTab] = useState<TabName>('open');
  const [sites, setSites] = useState<Site[]>([]);
  const tokenRef = useRef(token);
  useEffect(() => {
    tokenRef.current = token;
  }, [token]);
  const canAck = permissions.includes('alert.ack');
  const canEdit = permissions.includes('alert_rule.write');

  useEffect(() => {
    const controller = new AbortController();
    fetchSites(apiUrl, tokenRef.current, tenantId, controller.signal)
      .then(setSites)
      .catch(() => undefined);
    return () => {
      controller.abort();
    };
  }, [apiUrl, tenantId]);

  const tabs: { key: TabName; label: string }[] = [
    { key: 'open', label: 'Open' },
    { key: 'history', label: 'History' },
    { key: 'rules', label: 'Rules' },
  ];
  if (canEdit) tabs.push({ key: 'channels', label: 'Notifications' });

  return (
    <Box data-testid="alerts-page">
      <PageHeader
        title="Alerts"
        subtitle="Appliances and lighting endpoints that need attention."
      />
      <Tabs
        value={tab}
        onChange={(_, v: TabName) => {
          setTab(v);
        }}
        variant="scrollable"
        allowScrollButtonsMobile
        sx={{ mb: 2 }}
      >
        {tabs.map((t) => (
          <Tab key={t.key} value={t.key} label={t.label} data-testid={`alerts-tab-${t.key}`} />
        ))}
      </Tabs>
      {(tab === 'open' || tab === 'history') && (
        <AlertList
          key={tab}
          apiUrl={apiUrl}
          tenantId={tenantId}
          tokenRef={tokenRef}
          state={tab === 'open' ? 'open' : 'resolved'}
          canAck={canAck}
          now={now}
        />
      )}
      {tab === 'rules' && (
        <Rules
          apiUrl={apiUrl}
          tenantId={tenantId}
          tokenRef={tokenRef}
          sites={sites}
          canEdit={canEdit}
        />
      )}
      {tab === 'channels' && canEdit && (
        <Channels apiUrl={apiUrl} tenantId={tenantId} tokenRef={tokenRef} />
      )}
    </Box>
  );
}

// ---------------------------------------------------------------- alerts

type ListLoad =
  { kind: 'loading' } | { kind: 'error'; error: string } | { kind: 'ready'; alerts: AlertItem[] };

function AlertList({
  apiUrl,
  tenantId,
  tokenRef,
  state,
  canAck,
  now,
}: {
  apiUrl: string;
  tenantId: string;
  tokenRef: React.RefObject<string>;
  state: 'open' | 'resolved';
  canAck: boolean;
  now?: () => Date;
}) {
  const [load, setLoad] = useState<ListLoad>({ kind: 'loading' });
  const [acking, setAcking] = useState<AlertItem | null>(null);
  const [note, setNote] = useState('');
  const [details, setDetails] = useState<AlertItem | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const reload = useCallback(
    (signal?: AbortSignal) => {
      fetchAlerts(apiUrl, tokenRef.current, tenantId, state, signal)
        .then((alerts) => {
          setLoad({ kind: 'ready', alerts });
        })
        .catch((err: unknown) => {
          if (!signal?.aborted) setLoad({ kind: 'error', error: errorText(err) });
        });
    },
    [apiUrl, tenantId, state, tokenRef],
  );

  useEffect(() => {
    const controller = new AbortController();
    reload(controller.signal);
    if (state !== 'open') {
      return () => {
        controller.abort();
      };
    }
    const id = setInterval(() => {
      reload(controller.signal);
    }, REFRESH_MS);
    return () => {
      controller.abort();
      clearInterval(id);
    };
  }, [reload, state]);

  if (load.kind === 'loading')
    return <Typography color="text.secondary">Loading alerts…</Typography>;
  if (load.kind === 'error')
    return <Alert severity="error">Could not load alerts: {load.error}</Alert>;
  if (load.alerts.length === 0) {
    return state === 'open' ? (
      <EmptyState title="All clear" testId="alerts-empty">
        No open alerts. You&apos;ll see appliances and lighting endpoints here when they stop
        responding.
      </EmptyState>
    ) : (
      <EmptyState title="No history yet" testId="alerts-empty">
        Resolved alerts appear here, with how long each one lasted.
      </EmptyState>
    );
  }
  const nowMs = (now ? now() : new Date()).getTime();

  const unack = (a: AlertItem) => {
    acknowledge(apiUrl, tokenRef.current, tenantId, a.id, false)
      .then(() => {
        reload();
      })
      .catch((err: unknown) => {
        setMessage(errorText(err));
      });
  };

  return (
    <>
      <Stack spacing={1.5} data-testid={`alerts-${state}`}>
        {load.alerts.map((a) => (
          <Card key={a.id} variant="outlined" data-testid="alert-item">
            <CardContent sx={{ '&:last-child': { pb: 2 } }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                sx={{ alignItems: { sm: 'center' } }}
              >
                <Box sx={{ flexShrink: 0 }}>
                  <SeverityChip severity={a.severity} />
                </Box>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
                    {a.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <Link
                      href={routePath({ name: 'site', siteId: a.site_id })}
                      onClick={linkHandler(routePath({ name: 'site', siteId: a.site_id }))}
                      color="inherit"
                    >
                      {a.site_name}
                    </Link>
                    {' · '}
                    {a.resolved_at
                      ? `${when(a.condition_since)}, lasted ${formatDuration(Date.parse(a.resolved_at) - Date.parse(a.condition_since))}`
                      : `since ${when(a.condition_since)} (${formatDuration(nowMs - Date.parse(a.condition_since))})`}
                  </Typography>
                  {a.acknowledged_at && (
                    <Typography variant="body2" color="text.secondary" data-testid="alert-acked">
                      Acknowledged by {a.acknowledged_by ?? 'a former member'} at{' '}
                      {when(a.acknowledged_at)}
                      {a.ack_note ? `: “${a.ack_note}”` : ''}
                    </Typography>
                  )}
                </Box>
                <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                  <Button
                    size="small"
                    onClick={() => {
                      setDetails(a);
                    }}
                  >
                    Details
                  </Button>
                  {canAck && !a.resolved_at && !a.acknowledged_at && (
                    <Button
                      size="small"
                      variant="contained"
                      data-testid="alert-ack"
                      onClick={() => {
                        setNote('');
                        setAcking(a);
                      }}
                    >
                      Acknowledge
                    </Button>
                  )}
                  {canAck && !a.resolved_at && a.acknowledged_at && (
                    <Button
                      size="small"
                      data-testid="alert-unack"
                      onClick={() => {
                        unack(a);
                      }}
                    >
                      Undo acknowledge
                    </Button>
                  )}
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>
      <FormDialog
        open={acking !== null}
        title="Acknowledge alert"
        submitLabel="Acknowledge"
        submitTestId="alert-ack-submit"
        onClose={() => {
          setAcking(null);
        }}
        onSubmit={async () => {
          if (!acking) return;
          await acknowledge(apiUrl, tokenRef.current, tenantId, acking.id, true, note);
          setAcking(null);
          reload();
        }}
      >
        <Typography>{acking?.title}</Typography>
        <Typography variant="body2" color="text.secondary">
          No more notifications are sent about it until it resolves. If it happens again after that,
          it&apos;s a new alert.
        </Typography>
        <TextField
          label="Note (optional)"
          placeholder="e.g. service call booked"
          value={note}
          onChange={(e) => {
            setNote(e.target.value);
          }}
          slotProps={{ htmlInput: { maxLength: 500, 'data-testid': 'alert-ack-note' } }}
          fullWidth
        />
      </FormDialog>
      {details && (
        <AlertDetails
          apiUrl={apiUrl}
          tenantId={tenantId}
          tokenRef={tokenRef}
          alert={details}
          onClose={() => {
            setDetails(null);
          }}
        />
      )}
      <Snackbar
        open={message !== null}
        autoHideDuration={6000}
        message={message}
        onClose={() => {
          setMessage(null);
        }}
      />
    </>
  );
}

const EVENT_LABELS: Record<AlertEvent['event'], string> = {
  raised: 'Raised',
  acknowledged: 'Acknowledged',
  unacknowledged: 'Acknowledgement undone',
  resolved: 'Resolved',
  notified: 'Notification sent',
  notify_failed: 'Notification failed',
};

function AlertDetails({
  apiUrl,
  tenantId,
  tokenRef,
  alert,
  onClose,
}: {
  apiUrl: string;
  tenantId: string;
  tokenRef: React.RefObject<string>;
  alert: AlertItem;
  onClose: () => void;
}) {
  const [events, setEvents] = useState<AlertEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    fetchAlert(apiUrl, tokenRef.current, tenantId, alert.id, controller.signal)
      .then((r) => {
        setEvents(r.events);
      })
      .catch((err: unknown) => {
        if (!controller.signal.aborted) setError(errorText(err));
      });
    return () => {
      controller.abort();
    };
  }, [apiUrl, tenantId, alert.id, tokenRef]);
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{alert.title}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error">{error}</Alert>}
        {!events && !error && <Typography color="text.secondary">Loading…</Typography>}
        {events && (
          <Stack spacing={1} data-testid="alert-events">
            {events.map((e, i) => (
              <Stack key={i} direction="row" spacing={2} sx={{ justifyContent: 'space-between' }}>
                <Typography variant="body2">
                  {EVENT_LABELS[e.event]}
                  {e.actor ? ` by ${e.actor}` : ''}
                  {typeof e.details.note === 'string' ? `: “${e.details.note}”` : ''}
                  {typeof e.details.error === 'string' ? ` (${e.details.error})` : ''}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
                  {when(e.ts)}
                </Typography>
              </Stack>
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------- rules

type RulesLoad =
  | { kind: 'loading' }
  | { kind: 'error'; error: string }
  | { kind: 'ready'; rules: Rule[]; channels: Channel[] | null };

const KINDS: RuleKind[] = ['appliance_offline', 'endpoint_offline', 'telemetry_absent'];
const SEVERITIES: Severity[] = ['critical', 'warning', 'info'];

function Rules({
  apiUrl,
  tenantId,
  tokenRef,
  sites,
  canEdit,
}: {
  apiUrl: string;
  tenantId: string;
  tokenRef: React.RefObject<string>;
  sites: Site[];
  canEdit: boolean;
}) {
  const [load, setLoad] = useState<RulesLoad>({ kind: 'loading' });
  const [editing, setEditing] = useState<{ id?: string; draft: RuleInput } | null>(null);
  const [deleting, setDeleting] = useState<Rule | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const reload = useCallback(
    (signal?: AbortSignal) => {
      const t = tokenRef.current;
      Promise.all([
        fetchRules(apiUrl, t, tenantId, signal),
        canEdit ? fetchChannels(apiUrl, t, tenantId, signal) : Promise.resolve(null),
      ])
        .then(([rules, channels]) => {
          setLoad({ kind: 'ready', rules, channels });
        })
        .catch((err: unknown) => {
          if (!signal?.aborted) setLoad({ kind: 'error', error: errorText(err) });
        });
    },
    [apiUrl, tenantId, tokenRef, canEdit],
  );
  useEffect(() => {
    const controller = new AbortController();
    reload(controller.signal);
    return () => {
      controller.abort();
    };
  }, [reload]);

  if (load.kind === 'loading')
    return <Typography color="text.secondary">Loading rules…</Typography>;
  if (load.kind === 'error')
    return <Alert severity="error">Could not load rules: {load.error}</Alert>;
  const { rules, channels } = load;
  const siteName = (id: string | null) =>
    id === null ? 'All sites' : (sites.find((s) => s.id === id)?.name ?? 'One site');
  const channelNames = (ids: string[]) =>
    ids.map((id) => channels?.find((c) => c.id === id)?.name ?? 'a channel').join(', ');

  const toggle = (r: Rule) => {
    const { id, ...rest } = r;
    saveRule(apiUrl, tokenRef.current, tenantId, { ...rest, enabled: !r.enabled }, id)
      .then(() => {
        reload();
      })
      .catch((err: unknown) => {
        setMessage(errorText(err));
      });
  };

  return (
    <>
      {canEdit && (
        <Stack direction="row" sx={{ justifyContent: 'flex-end', mb: 2 }}>
          <Button
            variant="contained"
            data-testid="rule-add"
            onClick={() => {
              setEditing({
                draft: {
                  name: '',
                  kind: 'appliance_offline',
                  severity: 'warning',
                  for_seconds: 300,
                  enabled: true,
                  site_id: null,
                  channel_ids: [],
                },
              });
            }}
          >
            Add rule
          </Button>
        </Stack>
      )}
      {channels && channels.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Alerts show here but nobody is notified yet. Add an email recipient list under
          Notifications, then choose it on the rules that should send email.
        </Alert>
      )}
      <Stack spacing={1.5} data-testid="rules">
        {rules.map((r) => (
          <Card key={r.id} variant="outlined" data-testid="rule-item">
            <CardContent sx={{ '&:last-child': { pb: 2 } }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                sx={{ alignItems: { sm: 'center' } }}
              >
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ alignItems: 'center', flexWrap: 'wrap' }}
                  >
                    <Typography sx={{ fontWeight: 600 }}>{r.name}</Typography>
                    <SeverityChip severity={r.severity} />
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {KIND_LABELS[r.kind]} for {formatHold(r.for_seconds)} · {siteName(r.site_id)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {r.channel_ids.length === 0
                      ? 'Not notified'
                      : channels
                        ? `Notifies ${channelNames(r.channel_ids)}`
                        : `Notifies ${String(r.channel_ids.length)} channel(s)`}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexShrink: 0 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={r.enabled}
                        disabled={!canEdit}
                        onChange={() => {
                          toggle(r);
                        }}
                        slotProps={{ input: { 'aria-label': `${r.name} enabled` } }}
                      />
                    }
                    label={r.enabled ? 'On' : 'Off'}
                  />
                  {canEdit && (
                    <>
                      <Button
                        size="small"
                        data-testid="rule-edit"
                        onClick={() => {
                          const { id, ...draft } = r;
                          setEditing({ id, draft });
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => {
                          setDeleting(r);
                        }}
                      >
                        Delete
                      </Button>
                    </>
                  )}
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>
      {editing && (
        <RuleDialog
          initial={editing.draft}
          isNew={!editing.id}
          sites={sites}
          channels={channels ?? []}
          onClose={() => {
            setEditing(null);
          }}
          onSave={async (draft) => {
            await saveRule(apiUrl, tokenRef.current, tenantId, draft, editing.id);
            setEditing(null);
            reload();
          }}
        />
      )}
      <ConfirmDialog
        open={deleting !== null}
        title={`Delete rule “${deleting?.name ?? ''}”?`}
        confirmLabel="Delete"
        onConfirm={async () => {
          if (deleting) await deleteRule(apiUrl, tokenRef.current, tenantId, deleting.id);
          setDeleting(null);
          reload();
        }}
        onClose={() => {
          setDeleting(null);
        }}
      >
        Its open alerts are resolved, and it stops raising new ones. Past alerts stay in the
        history.
      </ConfirmDialog>
      <Snackbar
        open={message !== null}
        autoHideDuration={6000}
        message={message}
        onClose={() => {
          setMessage(null);
        }}
      />
    </>
  );
}

function RuleDialog({
  initial,
  isNew,
  sites,
  channels,
  onClose,
  onSave,
}: {
  initial: RuleInput;
  isNew: boolean;
  sites: Site[];
  channels: Channel[];
  onClose: () => void;
  onSave: (r: RuleInput) => Promise<void>;
}) {
  const [draft, setDraft] = useState<RuleInput>(initial);
  const [minutes, setMinutes] = useState(String(Math.round(initial.for_seconds / 60)));
  const mins = Number(minutes);
  const validMinutes = Number.isInteger(mins) && mins >= 1 && mins <= 1440;
  const set = (patch: Partial<RuleInput>) => {
    setDraft((d) => ({ ...d, ...patch }));
  };
  return (
    <FormDialog
      open
      title={isNew ? 'Add alert rule' : 'Edit alert rule'}
      submitLabel={isNew ? 'Add' : 'Save'}
      submitTestId="rule-save"
      canSubmit={draft.name.trim() !== '' && validMinutes}
      onClose={onClose}
      onSubmit={() => onSave({ ...draft, name: draft.name.trim(), for_seconds: mins * 60 })}
    >
      <TextField
        label="Name"
        value={draft.name}
        required
        onChange={(e) => {
          set({ name: e.target.value });
        }}
        slotProps={{ htmlInput: { maxLength: 100, 'data-testid': 'rule-name' } }}
        fullWidth
      />
      <TextField
        select
        label="When"
        value={draft.kind}
        onChange={(e) => {
          set({ kind: e.target.value as RuleKind });
        }}
        fullWidth
      >
        {KINDS.map((k) => (
          <MenuItem key={k} value={k}>
            {KIND_LABELS[k]}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        label="For at least (minutes)"
        type="number"
        value={minutes}
        error={!validMinutes}
        helperText={validMinutes ? 'Shorter outages don’t raise an alert.' : '1 to 1440 minutes'}
        onChange={(e) => {
          setMinutes(e.target.value);
        }}
        slotProps={{ htmlInput: { min: 1, max: 1440, 'data-testid': 'rule-minutes' } }}
        fullWidth
      />
      <TextField
        select
        label="Severity"
        value={draft.severity}
        onChange={(e) => {
          set({ severity: e.target.value as Severity });
        }}
        fullWidth
      >
        {SEVERITIES.map((s) => (
          <MenuItem key={s} value={s}>
            {SEVERITY[s].label}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        label="Sites"
        value={draft.site_id ?? ''}
        onChange={(e) => {
          set({ site_id: e.target.value === '' ? null : e.target.value });
        }}
        fullWidth
      >
        <MenuItem value="">All sites</MenuItem>
        {sites.map((s) => (
          <MenuItem key={s.id} value={s.id}>
            {s.name}
          </MenuItem>
        ))}
      </TextField>
      <Box>
        <Typography variant="body2" color="text.secondary">
          Notify
        </Typography>
        {channels.length === 0 ? (
          <Typography variant="body2">No notification channels yet.</Typography>
        ) : (
          channels.map((c) => (
            <FormControlLabel
              key={c.id}
              sx={{ display: 'flex' }}
              control={
                <Checkbox
                  checked={draft.channel_ids.includes(c.id)}
                  onChange={(e) => {
                    set({
                      channel_ids: e.target.checked
                        ? [...draft.channel_ids, c.id]
                        : draft.channel_ids.filter((id) => id !== c.id),
                    });
                  }}
                />
              }
              label={`${c.name} (${c.config.to.join(', ')})`}
            />
          ))
        )}
      </Box>
      <FormControlLabel
        control={
          <Switch
            checked={draft.enabled}
            onChange={(e) => {
              set({ enabled: e.target.checked });
            }}
          />
        }
        label="On"
      />
    </FormDialog>
  );
}

// ---------------------------------------------------------------- channels

type ChannelsLoad =
  { kind: 'loading' } | { kind: 'error'; error: string } | { kind: 'ready'; channels: Channel[] };

function Channels({
  apiUrl,
  tenantId,
  tokenRef,
}: {
  apiUrl: string;
  tenantId: string;
  tokenRef: React.RefObject<string>;
}) {
  const [load, setLoad] = useState<ChannelsLoad>({ kind: 'loading' });
  const [editing, setEditing] = useState<{
    id?: string;
    name: string;
    to: string;
    enabled: boolean;
  } | null>(null);
  const [deleting, setDeleting] = useState<Channel | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const reload = useCallback(
    (signal?: AbortSignal) => {
      fetchChannels(apiUrl, tokenRef.current, tenantId, signal)
        .then((channels) => {
          setLoad({ kind: 'ready', channels });
        })
        .catch((err: unknown) => {
          if (!signal?.aborted) setLoad({ kind: 'error', error: errorText(err) });
        });
    },
    [apiUrl, tenantId, tokenRef],
  );
  useEffect(() => {
    const controller = new AbortController();
    reload(controller.signal);
    return () => {
      controller.abort();
    };
  }, [reload]);

  if (load.kind === 'loading') return <Typography color="text.secondary">Loading…</Typography>;
  if (load.kind === 'error')
    return <Alert severity="error">Could not load channels: {load.error}</Alert>;

  const test = (c: Channel) => {
    testChannel(apiUrl, tokenRef.current, tenantId, c.id)
      .then(() => {
        setMessage(`Test email sent to ${c.config.to.join(', ')}.`);
      })
      .catch((err: unknown) => {
        setMessage(errorText(err));
      });
  };

  return (
    <>
      <Stack direction="row" sx={{ justifyContent: 'flex-end', mb: 2 }}>
        <Button
          variant="contained"
          data-testid="channel-add"
          onClick={() => {
            setEditing({ name: '', to: '', enabled: true });
          }}
        >
          Add email recipients
        </Button>
      </Stack>
      {load.channels.length === 0 ? (
        <EmptyState title="No notifications yet" testId="channels-empty">
          Add a list of email recipients, then choose it on the alert rules that should notify them.
        </EmptyState>
      ) : (
        <Stack spacing={1.5} data-testid="channels">
          {load.channels.map((c) => (
            <Card key={c.id} variant="outlined" data-testid="channel-item">
              <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1.5}
                  sx={{ alignItems: { sm: 'center' } }}
                >
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                      <Typography sx={{ fontWeight: 600 }}>{c.name}</Typography>
                      {!c.enabled && <Chip size="small" label="Paused" />}
                    </Stack>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ overflowWrap: 'anywhere' }}
                    >
                      Email to {c.config.to.join(', ')}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                    <Button
                      size="small"
                      onClick={() => {
                        test(c);
                      }}
                    >
                      Send test
                    </Button>
                    <Button
                      size="small"
                      onClick={() => {
                        setEditing({
                          id: c.id,
                          name: c.name,
                          to: c.config.to.join(', '),
                          enabled: c.enabled,
                        });
                      }}
                    >
                      Edit
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      onClick={() => {
                        setDeleting(c);
                      }}
                    >
                      Delete
                    </Button>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}
      {editing && (
        <FormDialog
          open
          title={editing.id ? 'Edit email recipients' : 'Add email recipients'}
          submitLabel={editing.id ? 'Save' : 'Add'}
          submitTestId="channel-save"
          canSubmit={editing.name.trim() !== '' && parseRecipients(editing.to).length > 0}
          onClose={() => {
            setEditing(null);
          }}
          onSubmit={async () => {
            await saveChannel(
              apiUrl,
              tokenRef.current,
              tenantId,
              {
                name: editing.name.trim(),
                kind: 'email',
                config: { to: parseRecipients(editing.to) },
                enabled: editing.enabled,
              },
              editing.id,
            );
            setEditing(null);
            reload();
          }}
        >
          <TextField
            label="Name"
            placeholder="e.g. Night shift"
            value={editing.name}
            required
            onChange={(e) => {
              setEditing({ ...editing, name: e.target.value });
            }}
            slotProps={{ htmlInput: { maxLength: 100, 'data-testid': 'channel-name' } }}
            fullWidth
          />
          <TextField
            label="Email addresses"
            placeholder="ops@example.com, tech@example.com"
            helperText="Separate addresses with commas or new lines (up to 20)."
            value={editing.to}
            required
            multiline
            minRows={2}
            onChange={(e) => {
              setEditing({ ...editing, to: e.target.value });
            }}
            slotProps={{ htmlInput: { 'data-testid': 'channel-to' } }}
            fullWidth
          />
          <FormControlLabel
            control={
              <Switch
                checked={editing.enabled}
                onChange={(e) => {
                  setEditing({ ...editing, enabled: e.target.checked });
                }}
              />
            }
            label="Send notifications"
          />
        </FormDialog>
      )}
      <ConfirmDialog
        open={deleting !== null}
        title={`Delete “${deleting?.name ?? ''}”?`}
        confirmLabel="Delete"
        onConfirm={async () => {
          if (deleting) await deleteChannel(apiUrl, tokenRef.current, tenantId, deleting.id);
          setDeleting(null);
          reload();
        }}
        onClose={() => {
          setDeleting(null);
        }}
      >
        Rules stop emailing {deleting?.config.to.join(', ')}. Alerts themselves aren&apos;t
        affected.
      </ConfirmDialog>
      <Snackbar
        open={message !== null}
        autoHideDuration={6000}
        message={message}
        onClose={() => {
          setMessage(null);
        }}
      />
    </>
  );
}
